import React, { useCallback, useEffect, useMemo, useState } from 'react';
import OriginTracer from '@atlassiansox/origin-tracing';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { AvatarProps } from '@atlaskit/avatar-group';
import Button, { LinkButton } from '@atlaskit/button/new';
import Image from '@atlaskit/image';
import { Box, xcss, Anchor } from '@atlaskit/primitives';
import { Modal } from '@atlassian/growth-pattern-library-modal';
import { UserLimits } from '@atlassian/growth-pattern-library-user-limits';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequestWithRetry } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useExperimentEligibility } from '@atlassian/jira-loss-aversion/src/services/check-experiment-eligibility/index.tsx';
import { useTrialDetails } from '@atlassian/jira-loss-aversion/src/services/check-experiment-eligibility/useTrialDetails.tsx';
import {
	fireOperationalAnalytics,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import type { userLimitModalQuery } from '@atlassian/jira-relay/src/__generated__/userLimitModalQuery.graphql';
import type { User } from '@atlassian/jira-shared-types/src/rest/jira/user.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fireButtonAnalytics } from '../../../common/utils/analytics';
import { EXPERIMENT_NAME_AB } from '../../../common/utils/constants';
import starsIcon from './assets/user_stars.svg';
import messages from './messages';
import type { Props } from './types';

export const UserLimitModal = ({ userCanPay, upgradeUrl, userCount }: Props) => (
	<JSErrorBoundary
		id="userLimitModal"
		packageName="jiraLossAversion"
		teamName="luna-growth"
		fallback="unmount"
	>
		<UserLimitModalContent userCount={userCount} userCanPay={userCanPay} upgradeUrl={upgradeUrl} />
	</JSErrorBoundary>
);

export const UserLimitModalContent = ({ userCanPay, upgradeUrl, userCount }: Props) => {
	const [showModal, setShowModal] = useState(true);

	// since we're loading this modal, we must be eligible for the experiment
	// and therefore do not have payment on file
	const { isUrgent } = useExperimentEligibility(EXPERIMENT_NAME_AB, false);
	const onClose = () => setShowModal(false);
	const { trialEndTime, trialDaysLeft } = useTrialDetails();
	const { formatMessage, formatDate } = useIntl();

	const [userAvatars, setUserAvatars] = useState<AvatarProps[]>();
	const [loadingAvatars, setLoadingAvatars] = useState(true);
	const [avatarsError, setAvatarsError] = useState(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const trialEndDate =
		trialEndTime && formatDate(new Date(trialEndTime), { day: 'numeric', month: 'long' });

	const cloudId = useCloudId();
	const addPaymentUrl =
		upgradeUrl || `https://admin.atlassian.com/billing/${cloudId}/payment-methods`;
	const origin = new OriginTracer({ product: 'jira' });
	const addPaymentDetailsUrl = origin.addToUrl(addPaymentUrl);

	const REMOVE_USERS_LINK = '/admin/users?referrerName=JIRA';

	const data = useLazyLoadQuery<userLimitModalQuery>(
		graphql`
			query userLimitModalQuery($cloudId: ID!) {
				jira {
					allJiraProjects(
						cloudId: $cloudId
						filter: { types: SOFTWARE, sortBy: { order: ASC, sortBy: KEY } }
						first: 100
					) {
						edges {
							node {
								key
							}
						}
					}
				}
			}
		`,
		{ cloudId },
	);

	const projectKeys = useMemo(
		() => data?.jira?.allJiraProjects?.edges?.map((edge) => edge?.node?.key) || [],
		[data],
	);
	const onAddPaymentDetails = useCallback(() => {
		fireButtonAnalytics(createAnalyticsEvent, 'jlaAddPaymentButton', {
			trialDaysLeft,
			userCanPay,
			modal: 'jlaUserModal',
		});

		setShowModal(false);
	}, [createAnalyticsEvent, trialDaysLeft, userCanPay]);

	const onRemoveUsers = useCallback(() => {
		fireButtonAnalytics(createAnalyticsEvent, 'jlaRemoveUsers', {
			trialDaysLeft,
			userCanPay,
			modal: 'jlaUserModal',
		});
	}, [createAnalyticsEvent, trialDaysLeft, userCanPay]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const usersResponse: User[] = await performGetRequestWithRetry(
					`/rest/api/3/user/assignable/multiProjectSearch?projectKeys=${projectKeys.join(',')}&maxResults=100`,
				);
				const userData: AvatarProps[] = usersResponse?.map((user) => ({
					key: user.emailAddress || '',
					name: user.displayName || '',
					src: user.avatarUrls['48x48'],
				}));

				fireOperationalAnalytics(createAnalyticsEvent({}), 'fetched userAvatars', {});

				setUserAvatars(userData);
				setShowModal(true);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'fetchUserAvatarsWithRetry',
						packageName: 'jiraLossAversion',
					},
				});
				setAvatarsError(error.message || 'Unknown avatar fetch error');
			} finally {
				setLoadingAvatars(false);
			}
		};

		fetchData();
	}, [projectKeys, createAnalyticsEvent, trialDaysLeft, userCanPay]);

	const getSubTitleText = useCallback((): React.ReactNode => {
		if (userCanPay) {
			return (
				<FormattedMessage
					{...(isUrgent
						? messages.jswStandardUserModalSubTitleUrgent
						: messages.jswStandardUserModalSubTitle)}
					values={{
						a: (chunks) => (
							<Anchor href={REMOVE_USERS_LINK} onClick={onRemoveUsers} target="_blank">
								{chunks}
							</Anchor>
						),
						trialEndDate,
					}}
				/>
			);
		}
		return (
			<FormattedMessage
				{...(isUrgent
					? messages.jswStandardUserModalSubTitleNoPayUrgent
					: messages.jswStandardUserModalSubTitleNoPay)}
				values={{
					trialEndDate,
				}}
			/>
		);
	}, [isUrgent, trialEndDate, userCanPay, onRemoveUsers]);

	const getTitleText = useCallback((): string => {
		if (isUrgent) {
			return formatMessage(messages.jswStandardUserModalTitleUrgent);
		}

		if (userCanPay) {
			return formatMessage(messages.jswStandardUserModalTitle);
		}
		return formatMessage(messages.jswStandardUserModalTitleNoPay);
	}, [formatMessage, isUrgent, userCanPay]);

	if (!showModal) {
		return <></>;
	}

	return (
		<Box xcss={modalStyles}>
			<ContextualAnalyticsData sourceType={MODAL} sourceName="jiraLossAversionUser">
				<Modal
					width={488}
					title={getTitleText()}
					subtitle={getSubTitleText()}
					onClose={onClose}
					testId="loss-aversion.ui.loss-aversion-modal.user-limit"
					actions={
						userCanPay && addPaymentDetailsUrl ? (
							<>
								<Button onClick={onClose} appearance="subtle">
									{formatMessage(messages.jswStandardSecondaryCtaText)}
								</Button>

								<LinkButton
									onClick={onAddPaymentDetails}
									appearance="primary"
									href={addPaymentDetailsUrl}
									target="_blank"
								>
									{formatMessage(messages.jswStandardPrimaryCtaText)}
								</LinkButton>
							</>
						) : (
							<Button onClick={onClose} appearance="subtle">
								{formatMessage(messages.jswStandardPrimaryCtaTextNoPay)}
							</Button>
						)
					}
				>
					{!loadingAvatars && !avatarsError && (
						<Box xcss={userAvatarStyles}>
							<UserLimits
								dividerLimit={10}
								assignedUsers={userAvatars || userCount}
								limit={10}
								title={formatMessage(messages.jswStandardUserAvatarTitle, {
									userCount,
								})}
								description={formatMessage(messages.jswStandardUserAvatarSubTitle)}
							/>
							<Image
								src={starsIcon}
								// eslint-disable-next-line jira/react/no-style-attribute
								style={{
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
									position: 'absolute',
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
									marginLeft: '275px',
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
									marginTop: '-20px',
								}}
								alt=""
								height={54}
								width={105}
							/>
						</Box>
					)}
				</Modal>
				<FireScreenAnalytics
					attributes={{ experiment: EXPERIMENT_NAME_AB, trialDaysLeft, userCanPay }}
				/>
			</ContextualAnalyticsData>
		</Box>
	);
};

const userAvatarStyles = xcss({
	display: 'flex',
	padding: 'space.300',
	maxWidth: '400px',
	flexDirection: 'column',
	borderRadius: '4.2px',
	gap: 'space.150',
	backgroundColor: 'color.background.accent.blue.subtlest',
});

const modalStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-end',
	alignItems: 'flex-end',
	maxWidth: '499px',
	gap: 'space.0',
	flex: '1 0 0',
});
